import {
  AccessTime as AccessTimeIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon,
  Download as DownloadIcon,
  PersonOutline as PersonOutlineIcon,
  Send as SendIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { CircularProgress, Grid, Menu, MenuItem, Tab, Tabs, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Button } from 'src/UIKit';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Breadcrumb } from 'src/components/Breadcrumb';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import { getTimePassed } from 'src/constants/helpers';
import { handleDownload } from 'src/helpers/helpers';
import { useCampaignsApi } from 'src/modules/campaigns/api/CampaignsApi';
import { SurveyDetails, SurveyExportFormat } from 'src/modules/campaigns/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { QuestionResponsesView } from 'src/modules/surveyResponses/components/SurveyDetailsView/QuestionResponsesView';
import { SurveyTimePeriodFilter } from 'src/modules/surveyResponses/components/SurveyTimePeriodFilter';
import { SurveyTimePeriodFilterValue } from 'src/modules/surveyResponses/types/SurveyTimePeriod';
import { RootState } from 'src/store';
import { logSafeAxiosError, reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { GENERIC_ERROR_MESSAGE } from 'src/constants/messages';

export const SurveyDetailsView = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const listSurveyFilter = useSelector((state: RootState) => state.surveyResponses.filter);
  const { getSurvey, getSurveyExport } = useCampaignsApi();

  const [surveyFilter, setSurveyFilter] = useState<SurveyTimePeriodFilterValue>(() => ({
    timePeriod: listSurveyFilter.timePeriod,
    startDateInterval: listSurveyFilter.startDateInterval,
    endDateInterval: listSurveyFilter.endDateInterval,
  }));
  const [surveyDetails, setSurveyDetails] = useState<SurveyDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);

  useEffect(() => {
    if (!surveyId || !currentCompanyId) {
      return;
    }

    let isActive = true;
    setIsLoading(true);
    setLoadError(null);

    getSurvey(surveyId, currentCompanyId, {
      startDateInterval: surveyFilter.startDateInterval,
      endDateInterval: surveyFilter.endDateInterval,
    })
      .then(response => {
        if (!isActive) {
          return;
        }
        setSurveyDetails(response.data);
      })
      .catch(error => {
        if (!isActive) {
          return;
        }
        logSafeAxiosError(error);
        setLoadError('Failed to load survey details.');
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    surveyId,
    currentCompanyId,
    getSurvey,
    surveyFilter.startDateInterval,
    surveyFilter.endDateInterval,
  ]);

  const handleDownloadExport = async (format: SurveyExportFormat) => {
    if (!surveyId) {
      return;
    }
    try {
      const response = await getSurveyExport(surveyId, currentCompanyId, format, {
        startDateInterval: surveyFilter.startDateInterval,
        endDateInterval: surveyFilter.endDateInterval,
      });
      handleDownload(response);
    } catch (error) {
      reportErrorToUser(error, GENERIC_ERROR_MESSAGE);
    }
    setDownloadMenuAnchor(null);
  };

  const handleTimePeriodChange = (value: SurveyTimePeriodFilterValue) => {
    setSurveyFilter(value);
  };

  const questionsCount = surveyDetails?.questions?.length ?? 0;
  const responsesCount = surveyDetails?.totalReplies ?? 0;
  const numberOfContacts = surveyDetails?.numberOfContacts ?? 0;
  const numberOfRespondedContacts = surveyDetails?.numberOfRespondedContacts ?? 0;
  const numberOfFinishedContacts = surveyDetails?.numberOfFinishedContacts ?? 0;
  const completionRate = questionsCount > 0 && numberOfContacts > 0 ?
    Math.round((responsesCount * 100) / (numberOfContacts * questionsCount)) : 0;
  const questions = surveyDetails?.questions ?? [];
  const selectedQuestion = questions[selectedQuestionIndex];

  return (
    <>
      <PrivateLayoutView
        title={
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Breadcrumb
                items={[
                  { label: 'Surveys', path: '/surveys' },
                  { label: surveyDetails?.name ?? 'Survey Details', isActive: true },
                ]}
              />
            </Grid>
            <Grid
              item
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
              <SurveyTimePeriodFilter
                value={{
                  timePeriod: surveyFilter.timePeriod,
                  startDateInterval: surveyFilter.startDateInterval,
                  endDateInterval: surveyFilter.endDateInterval,
                }}
                onChange={handleTimePeriodChange}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={event => setDownloadMenuAnchor(event.currentTarget)}
                disabled={!surveyId}
              >
                Export All Responses
              </Button>
            </Grid>
          </Grid>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="center">
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ChatBubbleOutlineIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {questionsCount} questions
                </Typography>
              </Grid>
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonOutlineIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {responsesCount} responses
                </Typography>
              </Grid>
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingUpIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {completionRate}% completion rate
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="center">
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <SendIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {numberOfContacts} contacts
                </Typography>
              </Grid>
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {numberOfRespondedContacts} responded
                </Typography>
              </Grid>
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DoneAllIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {numberOfFinishedContacts} finished
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          {surveyDetails?.lastResponseAt && (
            <Grid item xs={12}>
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Last response {getTimePassed(surveyDetails.lastResponseAt)} ago
                </Typography>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            <Tabs
              value={selectedQuestionIndex}
              onChange={(_, value) => setSelectedQuestionIndex(value)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Survey questions"
            >
              {questions.map((_, index) => (
                <Tab key={`question-${index}`} label={`Q${index + 1}`} />
              ))}
            </Tabs>
          </Grid>
          <Grid item xs={12}>
            <QuestionResponsesView
              questionNumber={selectedQuestionIndex + 1}
              totalQuestions={questions.length}
              question={selectedQuestion}
              companyId={currentCompanyId}
              startDateInterval={surveyFilter.startDateInterval}
              endDateInterval={surveyFilter.endDateInterval}
            />
          </Grid>
          {isLoading && (
            <Grid item xs={12}>
              <CircularProgress size={24} />
            </Grid>
          )}

          {loadError && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">
                {loadError}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Menu
          anchorEl={downloadMenuAnchor}
          open={Boolean(downloadMenuAnchor)}
          onClose={() => setDownloadMenuAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => handleDownloadExport('csv')}>
            Download Responses as CSV
          </MenuItem>
          <MenuItem onClick={() => handleDownloadExport('xlsx')}>
            Download Responses as XLSX
          </MenuItem>
        </Menu>
      </PrivateLayoutView>
    </>
  );
};
